import { activeMeasurementIdAtom, addMeasurementAtom, addPointModeAtom, updateMeasurementPointsAtom } from '@/atoms';
import { PointCloudOctree } from '@pnext/three-loader';
import { Billboard, Text } from '@react-three/drei';
import { ThreeEvent, useThree } from '@react-three/fiber';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as THREE from 'three';
import PointX from './PointX';

/** Slim cylinder drawn between two world-space points. */
const CylinderLine = ({
  start,
  end,
  color,
  opacity = 0.8,
  radius = 0.005,
}: {
  start: THREE.Vector3;
  end: THREE.Vector3;
  color: string;
  opacity?: number;
  radius?: number;
}) => {
  const { midpoint, length, quaternion } = useMemo(() => {
    const mid = start.clone().add(end).multiplyScalar(0.5);
    const len = start.distanceTo(end);
    const dir = new THREE.Vector3().subVectors(end, start).normalize();
    const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    return { midpoint: mid, length: len, quaternion: quat };
  }, [start, end]);

  if (length < 1e-6) return null;

  return (
    <mesh position={midpoint} quaternion={quaternion} renderOrder={Number.MAX_SAFE_INTEGER - 100}>
      <cylinderGeometry args={[radius, radius, length, 8, 1]} />
      <meshBasicMaterial color={color} transparent opacity={opacity} depthTest={false} />
    </mesh>
  );
};

const MeasuringTool = ({
  targets,
  projectId,
}: {
  origin: THREE.Vector3;
  targets: PointCloudOctree[];
  projectId?: string;
}) => {
  const [pickedPoints, setPickedPoints] = useState<THREE.Vector3[]>([]);
  const [hoverPoint, setHoverPoint] = useState<THREE.Vector3 | null>(null);
  const camera = useThree((state) => state.camera);
  const renderer = useThree((state) => state.gl);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const [params] = useSearchParams();
  const showMeasurements = params.get('showMeasurements') === 'true';
  const addPointMode = useAtomValue(addPointModeAtom);
  const addMeasurement = useSetAtom(addMeasurementAtom(projectId ?? ''));
  const updateMeasurementPoints = useSetAtom(updateMeasurementPointsAtom(projectId ?? ''));
  const setActiveMeasurementId = useSetAtom(activeMeasurementIdAtom);
  const savedIdRef = useRef<string | null>(null);
  const hasSavedRef = useRef(false);

  const saveMeasurement = useCallback(
    (points: THREE.Vector3[]) => {
      if (!projectId || points.length < 2) return;
      const id = addMeasurement({
        type: 'distance',
        points: points.map((p) => p.toArray() as [number, number, number]),
      });
      savedIdRef.current = id ?? null;
      setActiveMeasurementId(id ?? null);
    },
    [projectId, addMeasurement],
  );

  const pointerDownPos = useRef<{ x: number; y: number } | null>(null);

  // refs for dragging state and group references (changed from sphere to group)
  const draggingRef = useRef<{
    index: number;
    plane: THREE.Plane;
    offset: THREE.Vector3;
  } | null>(null);

  // Hover handler - show preview marker
  useEffect(() => {
    if (!showMeasurements || addPointMode?.enabled) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Calculate normalized device coordinates
      const rect = renderer.domElement.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      // Configure raycaster
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hits = targets.map((t) => t.pick(renderer, camera, raycaster.ray)).filter(Boolean);

      if (hits.length > 0 && hits[0]?.position) {
        setHoverPoint(hits[0].position.clone());
        renderer.domElement.style.cursor = 'crosshair';
      } else {
        setHoverPoint(null);
        renderer.domElement.style.cursor = 'default';
      }
    };

    renderer.domElement.addEventListener('pointermove', handleMouseMove);
    return () => {
      renderer.domElement.removeEventListener('pointermove', handleMouseMove);
      renderer.domElement.style.cursor = 'default';
    };
  }, [showMeasurements, addPointMode, targets, camera, renderer, raycaster]);

  useEffect(() => {
    if (!showMeasurements) return;
    const dom = renderer.domElement;
    const DRAG_THRESHOLD = 5;

    const handlePointerDown = (e: MouseEvent) => {
      pointerDownPos.current = { x: e.clientX, y: e.clientY };
    };

    const handlePointerUp = (e: MouseEvent) => {
      // Only react to left-click
      if (e.button !== 0) return;
      // Ignore camera drags — only react to clicks
      if (!pointerDownPos.current) return;
      const dx = e.clientX - pointerDownPos.current.x;
      const dy = e.clientY - pointerDownPos.current.y;
      pointerDownPos.current = null;
      if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) return;

      if (addPointMode?.enabled) return;
      if (draggingRef.current) return;

      const rect = dom.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hits = targets.map((t) => t.pick(renderer, camera, raycaster.ray)).filter(Boolean);

      if (hits.length === 0 || !hits[0]?.position) return;

      const world = new THREE.Vector3(...hits[0].position.toArray());
      setPickedPoints((prev) => {
        if (prev.length === 2) {
          hasSavedRef.current = false;
          savedIdRef.current = null;
          setActiveMeasurementId(null);
        }
        const pts = prev.length === 2 ? [] : prev;
        return [...pts, world];
      });

      // Clear hover point when a point is picked
      setHoverPoint(null);
    };

    dom.addEventListener('pointerdown', handlePointerDown);
    dom.addEventListener('pointerup', handlePointerUp);
    return () => {
      dom.removeEventListener('pointerdown', handlePointerDown);
      dom.removeEventListener('pointerup', handlePointerUp);
    };
  }, [targets, camera, renderer, raycaster, addPointMode, showMeasurements]);

  useEffect(() => {
    if (pickedPoints.length === 2 && !hasSavedRef.current) {
      hasSavedRef.current = true;
      saveMeasurement(pickedPoints);
    }
  }, [pickedPoints, saveMeasurement]);

  useEffect(() => {
    if (!showMeasurements) {
      hasSavedRef.current = false;
      savedIdRef.current = null;
      setActiveMeasurementId(null);
      setPickedPoints([]);
      setHoverPoint(null);
    }
  }, [showMeasurements]);

  // Right-click cancels the current measurement
  useEffect(() => {
    if (!showMeasurements) return;
    const dom = renderer.domElement;

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      hasSavedRef.current = false;
      savedIdRef.current = null;
      setActiveMeasurementId(null);
      setPickedPoints([]);
      setHoverPoint(null);
    };

    dom.addEventListener('contextmenu', handleContextMenu);
    return () => {
      dom.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [showMeasurements, renderer]);

  const startDrag = (e: ThreeEvent<PointerEvent>, index: number) => {
    e.stopPropagation();
    const point = e.point.clone();
    const camDir = new THREE.Vector3().copy(camera.getWorldDirection(new THREE.Vector3()));
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(camDir, point);
    const offset = point.clone().sub(pickedPoints[index]);
    draggingRef.current = { index, plane, offset };

    const dom = renderer.domElement;

    const onPointerMove = (ev: PointerEvent) => {
      ev.stopPropagation();
      const rect = dom.getBoundingClientRect();
      const x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const intersectPoint = new THREE.Vector3();
      if (raycaster.ray.intersectPlane(plane, intersectPoint)) {
        setPickedPoints((prev) => {
          const pts = [...prev];
          pts[index] = intersectPoint.clone().sub(offset);
          if (savedIdRef.current) {
            updateMeasurementPoints({
              id: savedIdRef.current,
              points: pts.map((p) => p.toArray() as [number, number, number]),
            });
          }
          return pts;
        });
      }
    };

    const onPointerUp = () => {
      window.removeEventListener('pointermove', onPointerMove, true);
      window.removeEventListener('pointerup', onPointerUp);

      const hits = targets.map((t) => t.pick(renderer, camera, raycaster.ray)).filter(Boolean);

      if (hits.length > 0 && hits[0]?.position) {
        const world = new THREE.Vector3(...hits[0].position.toArray());
        setPickedPoints((prev) => {
          const pts = [...prev];
          pts[index] = world;
          if (savedIdRef.current) {
            updateMeasurementPoints({
              id: savedIdRef.current,
              points: pts.map((p) => p.toArray() as [number, number, number]),
            });
          }
          return pts;
        });
      } else if (savedIdRef.current) {
        setPickedPoints((prev) => {
          if (savedIdRef.current) {
            updateMeasurementPoints({
              id: savedIdRef.current,
              points: prev.map((p) => p.toArray() as [number, number, number]),
            });
          }
          return prev;
        });
      }

      draggingRef.current = null;
    };

    window.addEventListener('pointermove', onPointerMove, true);
    window.addEventListener('pointerup', onPointerUp);
  };

  // Create triangle points for height visualization (world-space)
  const trianglePoints = useMemo(() => {
    if (pickedPoints.length < 2) return [];

    const baseZ = Math.min(pickedPoints[0].z, pickedPoints[1].z);
    const lowerPoint = pickedPoints[0].z < pickedPoints[1].z ? pickedPoints[0] : pickedPoints[1];
    const higherPoint = pickedPoints[0].z < pickedPoints[1].z ? pickedPoints[1] : pickedPoints[0];
    const cornerPoint = new THREE.Vector3(higherPoint.x, higherPoint.y, baseZ);

    return [lowerPoint.clone(), cornerPoint, higherPoint.clone()];
  }, [pickedPoints]);

  const heightLinePoints = useMemo(() => {
    if (pickedPoints.length < 2) return [];
    const baseZ = Math.min(pickedPoints[0].z, pickedPoints[1].z);
    const higherPoint = pickedPoints[0].z < pickedPoints[1].z ? pickedPoints[1] : pickedPoints[0];
    const cornerPoint = new THREE.Vector3(higherPoint.x, higherPoint.y, baseZ);
    return [cornerPoint, higherPoint.clone()];
  }, [pickedPoints]);

  const textPosition = useMemo(() => {
    if (pickedPoints.length < 2) return new THREE.Vector3();
    return pickedPoints[0].clone().add(pickedPoints[1]).multiplyScalar(0.5);
  }, [pickedPoints]);

  const fontSize = useMemo(() => {
    if (pickedPoints.length < 2) return 0;
    return textPosition.distanceTo(camera.position) / 50;
  }, [textPosition, pickedPoints, camera]);

  const heightTextPosition = useMemo(() => {
    if (pickedPoints.length < 2) return new THREE.Vector3();
    const baseZ = Math.min(pickedPoints[0].z, pickedPoints[1].z);
    const higherPoint = pickedPoints[0].z < pickedPoints[1].z ? pickedPoints[1] : pickedPoints[0];
    const cornerPoint = new THREE.Vector3(higherPoint.x, higherPoint.y, baseZ);
    return cornerPoint.clone().add(higherPoint).multiplyScalar(0.5);
  }, [pickedPoints]);

  const heightDifference = useMemo(() => {
    if (pickedPoints.length < 2) return 0;
    return Math.abs(pickedPoints[1].z - pickedPoints[0].z);
  }, [pickedPoints]);

  const distance2D = useMemo(() => {
    if (pickedPoints.length < 2) return 0;
    const dx = pickedPoints[1].x - pickedPoints[0].x;
    const dy = pickedPoints[1].y - pickedPoints[0].y;
    return Math.sqrt(dx * dx + dy * dy);
  }, [pickedPoints]);

  const distance2DTextPosition = useMemo(() => {
    if (pickedPoints.length < 2) return new THREE.Vector3();
    const baseZ = Math.min(pickedPoints[0].z, pickedPoints[1].z);
    const lowerPoint = pickedPoints[0].z < pickedPoints[1].z ? pickedPoints[0] : pickedPoints[1];
    const higherPoint = pickedPoints[0].z < pickedPoints[1].z ? pickedPoints[1] : pickedPoints[0];
    const cornerPoint = new THREE.Vector3(higherPoint.x, higherPoint.y, baseZ);
    return lowerPoint.clone().add(cornerPoint).multiplyScalar(0.5);
  }, [pickedPoints]);

  const isOrtho = camera.type === 'OrthographicCamera';
  const showHeightVisualization = heightDifference >= 0.01 && !isOrtho;

  const labelsAreTooClose = useMemo(() => {
    if (pickedPoints.length < 2 || !showHeightVisualization) return false;
    return textPosition.distanceTo(heightTextPosition) < fontSize * 5;
  }, [textPosition, heightTextPosition, fontSize, pickedPoints, showHeightVisualization]);

  const calculateScale = (point: THREE.Vector3) => {
    const d = camera.position.distanceTo(point);
    const pr = renderer.getPixelRatio();
    const height = renderer.domElement.clientHeight * pr;

    if (camera.type === 'OrthographicCamera') {
      const orthographicCamera = camera as THREE.OrthographicCamera;
      const worldScreenHeight = Math.abs(orthographicCamera.top - orthographicCamera.bottom) / orthographicCamera.zoom;
      const unitsPerPixel = worldScreenHeight / height;
      return 20 * unitsPerPixel;
    } else {
      const perspectiveCamera = camera as THREE.PerspectiveCamera;
      const vFOV = (perspectiveCamera.getEffectiveFOV() * Math.PI) / 180;
      const worldScreenHeight = 2 * d * Math.tan(vFOV / 2);
      const unitsPerPixel = worldScreenHeight / height;
      return 20 * unitsPerPixel;
    }
  };

  const scales = useMemo(() => pickedPoints.map(calculateScale), [pickedPoints, camera, renderer]);

  const hoverScale = useMemo(() => (hoverPoint ? calculateScale(hoverPoint) : 1), [hoverPoint, camera, renderer]);

  if (!showMeasurements) return null;

  return (
    <>
      {/* Hover preview marker - semi-transparent */}
      {hoverPoint && !draggingRef.current && <PointX scale={hoverScale} position={hoverPoint} />}

      {pickedPoints.map((point, i) => (
        <PointX key={i} scale={scales[i]} position={point} onPointerDown={(e) => startDrag(e, i)} />
      ))}

      {pickedPoints.length === 2 && (
        <>
          {/* Main distance line */}
          <CylinderLine start={pickedPoints[0]} end={pickedPoints[1]} color='red' />

          {showHeightVisualization && !draggingRef.current && (
            <>
              {/* Triangle visualization for height */}
              <CylinderLine start={trianglePoints[0]} end={trianglePoints[1]} color='blue' opacity={0.5} />
              <CylinderLine start={trianglePoints[1]} end={trianglePoints[2]} color='blue' opacity={0.5} />

              {/* Vertical height line */}
              <CylinderLine start={heightLinePoints[0]} end={heightLinePoints[1]} color='green' />
            </>
          )}

          {!draggingRef.current ? (
            isOrtho ? (
              <Billboard position={textPosition}>
                <Text
                  fontSize={fontSize}
                  color='white'
                  outlineColor='black'
                  outlineWidth={0.01}
                  outlineBlur={0.01}
                  strokeColor='red'
                  strokeWidth={0.001}
                  depthOffset={Number.MIN_SAFE_INTEGER}
                  renderOrder={Number.MAX_SAFE_INTEGER}
                  anchorX='center'
                  anchorY='middle'>
                  {pickedPoints[0].distanceTo(pickedPoints[1]).toFixed(2)}
                </Text>
              </Billboard>
            ) : labelsAreTooClose ? (
              <Billboard position={textPosition}>
                <Text
                  fontSize={fontSize}
                  color='white'
                  outlineColor='black'
                  outlineWidth={0.01}
                  outlineBlur={0.01}
                  strokeColor='red'
                  strokeWidth={0.001}
                  depthOffset={Number.MIN_SAFE_INTEGER}
                  renderOrder={Number.MAX_SAFE_INTEGER}
                  anchorX='center'
                  anchorY='middle'>
                  3D – {pickedPoints[0].distanceTo(pickedPoints[1]).toFixed(2)} | 2D – {distance2D.toFixed(2)} |{' '}
                  {'H – '}
                  {heightDifference.toFixed(2)}
                </Text>
              </Billboard>
            ) : (
              <>
                <Billboard position={textPosition}>
                  <Text
                    fontSize={fontSize}
                    color='white'
                    outlineColor='black'
                    outlineWidth={0.01}
                    outlineBlur={0.01}
                    strokeColor='red'
                    strokeWidth={0.001}
                    depthOffset={Number.MIN_SAFE_INTEGER}
                    renderOrder={Number.MAX_SAFE_INTEGER}
                    anchorX='center'
                    anchorY='middle'>
                    3D – {pickedPoints[0].distanceTo(pickedPoints[1]).toFixed(2)}
                  </Text>
                </Billboard>
                {showHeightVisualization && (
                  <>
                    <Billboard position={heightTextPosition}>
                      <Text
                        fontSize={fontSize}
                        color='white'
                        outlineColor='black'
                        outlineWidth={0.01}
                        outlineBlur={0.01}
                        strokeColor='green'
                        strokeWidth={0.001}
                        depthOffset={Number.MIN_SAFE_INTEGER}
                        renderOrder={Number.MAX_SAFE_INTEGER}
                        anchorX='center'
                        anchorY='middle'>
                        H – {heightDifference.toFixed(2)}
                      </Text>
                    </Billboard>
                    <Billboard position={distance2DTextPosition}>
                      <Text
                        fontSize={fontSize}
                        color='white'
                        outlineColor='black'
                        outlineWidth={0.01}
                        outlineBlur={0.01}
                        strokeColor='blue'
                        strokeWidth={0.001}
                        depthOffset={Number.MIN_SAFE_INTEGER}
                        renderOrder={Number.MAX_SAFE_INTEGER}
                        anchorX='center'
                        anchorY='middle'>
                        2D – {distance2D.toFixed(2)}
                      </Text>
                    </Billboard>
                  </>
                )}
              </>
            )
          ) : null}
        </>
      )}
    </>
  );
};

export default MeasuringTool;
